import { SortByAlpha } from '@mui/icons-material'
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined'
import LastPageIcon from '@mui/icons-material/LastPage'
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CONSTANTS } from '../../../Constants/Constant'
import { formatListingDate } from '../../../Helpers/ManageContent'
import { getStickyIndex } from '../../../Helpers/Util'
import { CommonTableHeaderModel, CommonTableModel } from '../../../Models/CommonTable'
import MUICheckbox from '../MUI-Checkbox'
import MUIGrid from '../MUI-Grid'
import MUIPagination from '../MUI-Pagination'
import { DescendingIcon } from '../MUI-Table/MUI-Table.svgicons'
import MUITooltip from '../MUI-Tooltip'
import SimpleDropdown from '../Simpledropdown'
import { TooltipText } from '../TooltipText'
import { FilterSortIcon, FilterUi } from './Helpers'
import css from './index.module.scss'

const getObjectValue = (obj: Record<string, any>, key: string): any => {
  const keys = key.split('.')

  return keys.reduce((acc: any, currentKey: any) => {
    if (!acc) return undefined

    if (Array.isArray(acc)) {
      const filteredValues = acc
        .filter(
          (ele: any) =>
            ele[currentKey] !== undefined && ele[currentKey] !== null && ele[currentKey] !== '',
        )
        .map((ele: any) => ele[currentKey])

      return filteredValues.length > 0 ? filteredValues : undefined
    }

    return acc[currentKey]
  }, obj)
}

const MUICommonTable: React.FC<CommonTableModel> = ({
  headers,
  defaultValue = CONSTANTS.DEFAULT_TEXT,
  rowsClickable = true,
  rows,
  childRows,
  className = '',
  noRecordsMessage = CONSTANTS.MESSAGES.NO_DATA_FOUND,
  sortFilterKey = 'id',
  selectedFilters = { sort: '', sortType: '', filters: [] },
  noBoxShadow,
  onChangeSort,
  onChangeFilter,
  onClearSortFilters,
  onClickRow,
  checkboxTooltip = {},
  transparent = false,
  pagination = {
    show: false,
  } as any,
  filterConOptions = [
    { label: 'Is equal to', value: 'equals' },
    { label: 'Is not equal to', value: 'notEquals' },
    { label: 'Starts With', value: 'startsWith' },
    { label: 'Ends With', value: 'endsWith' },
    { label: 'Contains', value: 'contains' },
  ],
  archived = false,
  showArchivalHistory = false,
  onChangeArchivalState = () => {
    return
  },
  loader = false,
  sticky,
  stickyWidth,
  maxCharacterLimit = 30,
  enableCheckbox = false,
  checkBoxKey = '_id',
  checkedItems,
  onCheckItems = () => {
    return
  },
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [sortFilterOpen, setSortFilterOpen] = useState<string | number | boolean>('')
  const sortOptions = [
    { lable: 'Ascending', value: 1, icon: <SortByAlpha style={{ fontSize: '18px' }} /> },
    { lable: 'Descending', value: -1, icon: <DescendingIcon /> },
  ]
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null)
  const [filterOpen, setFilterOpen] = useState<string | number | boolean>('')
  const [filterValue, setFilterValue] = useState<{
    filterBy: string | number
    filterValue: string | number
    filterCondition: string | number
  }>({ filterBy: '', filterValue: '', filterCondition: '' })

  const [tableHeaders, setTableHeaders] = useState(headers)
  const [selectedRows, setSelectedRows] = useState<any>([])
  const [selectableRows, setSelectabledRows] = useState<any>(rows)

  useEffect(() => {
    if (enableCheckbox) {
      const indexForCheckbox = headers?.findIndex((i) => i?.id === 'versionCloneCount') + 1
      setTableHeaders([
        ...headers.slice(0, indexForCheckbox),
        {
          class: 'list_checkbox',
          fieldName: '',
          icon: null,
          id: 'checkbox',
          label: '',
          sort: false,
        },
        ...headers.slice(indexForCheckbox),
      ])
    } else {
      setTableHeaders(headers)
    }
  }, [headers])

  useEffect(() => {
    if (enableCheckbox) {
      const selectableRows = rows?.filter((row: any) => !row.isCheckBoxDisabled) || []
      const selectableChildRows =
        childRows?.records?.filter((row: any) => !row.isCheckBoxDisabled) || []
      const selectableRowsCombined = [...selectableRows, ...selectableChildRows]
      setSelectabledRows(selectableRowsCombined)
      const updatedSelectedRows = selectableRowsCombined
        .filter((row: any) => checkedItems?.includes(row?.[checkBoxKey]))
        .map((i: any) => i?.[checkBoxKey])
      setSelectedRows(updatedSelectedRows)
    }
  }, [rows, childRows?.records, checkedItems])

  const handleSelectAll = (e: any) => {
    if (e?.target?.checked) {
      const updatedSelectedRows = selectableRows
        ?.filter((row: any) => !row.isCheckBoxDisabled)
        ?.map((row: any) => row?.[checkBoxKey])

      setSelectedRows(updatedSelectedRows)
      onCheckItems(updatedSelectedRows)
    } else {
      setSelectedRows([])
      onCheckItems([])
    }
  }

  const handleSelect = (selectedRow: any) => {
    if (selectedRows?.includes(selectedRow?.[checkBoxKey])) {
      setSelectedRows(selectedRows.filter((row: any) => selectedRow?.[checkBoxKey] !== row))
      onCheckItems(selectedRows.filter((row: any) => selectedRow?.[checkBoxKey] !== row))
    } else {
      const updatedSelectedRow = selectableRows
        ?.filter((row: any) => !row.isCheckBoxDisabled)
        ?.find((row: any) => selectedRow?.[checkBoxKey] === row?.[checkBoxKey])
      const uniqueSelectedRows = Array.from(
        new Set([...selectedRows, updatedSelectedRow?.[checkBoxKey]]),
      )
      onCheckItems(uniqueSelectedRows)
      setSelectedRows(uniqueSelectedRows)
    }
  }

  const handleSortFilterOpen = (ev: any, header: CommonTableHeaderModel) => {
    setSortFilterOpen(header.id)
    setAnchorEl(ev.currentTarget)
  }

  const handleFilterOpen = (ev: any, header: CommonTableHeaderModel) => {
    setFilterOpen(header.id)
    setAnchorEl2(ev.currentTarget)
    const selectedFilter = selectedFilters?.filters?.find(
      (item) => item.filterBy === header[sortFilterKey],
    )
    if (selectedFilter) {
      setFilterValue({ ...selectedFilter })
    }
  }

  const handleSortFilterClose = (header?: any, value?: number) => {
    setSortFilterOpen('')
    setAnchorEl(null)
    if (header && onChangeSort) {
      onChangeSort(
        selectedFilters?.sort === header[sortFilterKey] &&
          selectedFilters?.sortType === String(value)
          ? { ...header, fieldName: '' }
          : header,
        value,
      )
    }
  }

  const handleFilterClose = (header?: any, value?: any) => {
    handleSortFilterClose()
    setFilterOpen('')
    setAnchorEl2(null)
    if (header && onChangeFilter) {
      onChangeFilter(header, { ...value, filterBy: header[sortFilterKey] })
    }
    setFilterValue({ filterBy: '', filterValue: '', filterCondition: '' })
  }

  const renderTableValue = (row: Record<string, any>, header: Record<string, any>) => {
    const value = getObjectValue(row, header.id)
    if (value && header?.fieldType === CONSTANTS.FIELDTYPES.DATETIME) {
      return formatListingDate(value, CONSTANTS.DATE_FORMAT_TIME)
    } else if (value && header?.fieldType === CONSTANTS.FIELDTYPES.DATE) {
      return formatListingDate(value, CONSTANTS.DATE_FORMAT)
    } else if (header?.dataType === CONSTANTS.FIELDTYPES.BOOLEAN) {
      if (value === true) {
        return 'Yes'
      } else if (value === false) {
        return 'No'
      }
      return CONSTANTS.DEFAULT_TEXT
    } else if (React.isValidElement(value)) {
      return <span>{value}</span>
    } else if (value?.length && header?.dataType === 'IMAGE_TYPE') {
      return (
        <TooltipText text={`${CONSTANTS.CLOUDFRONT_BASE_URL}/${value.toString()}`} length={25} />
      )
    } else {
      switch (typeof value) {
        case 'string':
        case 'number':
        case 'boolean':
          if (typeof value === 'string' || typeof value === 'number') {
            return <TooltipText text={value.toString()} length={maxCharacterLimit} />
          } else {
            return <span>{value}</span>
          }
        default:
          if (
            header?.isMultiple &&
            Array.isArray(value) &&
            value.length &&
            typeof value[0] === 'string'
          ) {
            return <TooltipText text={value.join(', ')} length={25} />
          } else if (Array.isArray(value) && header?.dataType !== 'IMAGE_TYPE') {
            return <TooltipText text={value.join(', ') || CONSTANTS.DEFAULT_TEXT} length={25} />
          }
          return defaultValue
      }
    }
  }

  const sortFilterUi = (header: CommonTableHeaderModel) => {
    const isFilter = selectedFilters?.filters?.find(
      (item: any) => item?.filterBy === header[sortFilterKey] && header.filter,
    )
    const isFilterEnabled = Boolean(selectedFilters?.sort === header[sortFilterKey] || isFilter)
    const clearSortBy = async () => {
      setSortFilterOpen('')
      setAnchorEl(null)
      setAnchorEl2(null)
      if (isFilterEnabled) {
        onClearSortFilters?.(
          header,
          header.filter
            ? {
                filterValue: '',
                filterCondition: 'equals',
                filterBy: header[sortFilterKey],
              }
            : null,
        )
      }
    }

    return sortFilterOpen === header.id ? (
      <Menu
        className='border-12 c-sort'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleSortFilterClose()}
      >
        {' '}
        {header.sort && (
          <>
            <div className='d-flex justify-content-between align-items-center'>
              <Typography>Sort By</Typography>
              {onClearSortFilters && (
                <span
                  className={isFilterEnabled ? 'clear-all' : 'clear-all-disabled'}
                  data-testid='clear-all-sort'
                  onClick={() => {
                    clearSortBy()
                  }}
                >
                  Clear Filter
                </span>
              )}
            </div>
            <RadioGroup
              aria-label='options'
              name='options'
              value={
                selectedFilters.sort === header[sortFilterKey] ? selectedFilters.sortType : null
              }
            >
              {sortOptions.map((sortOption) => (
                <MenuItem
                  key={sortOption.lable}
                  sx={{
                    paddingLeft: 0,
                  }}
                  className='d-flex justify-content-between align-items-center'
                  onClick={() => {
                    handleSortFilterClose(header, sortOption.value)
                  }}
                  disabled={header?.fieldType === CONSTANTS.FIELDTYPES.SELECT && header?.isMultiple}
                >
                  <FormControlLabel
                    labelPlacement='start'
                    value={sortOption.value}
                    label={
                      <div className='d-flex w-100 align-item-center justify-content-center'>
                        <div className='d-flex align-items-center'>{sortOption.icon}</div>
                        <div className='ml-8p'>
                          <span className='font-12p'>{sortOption.lable}</span>
                        </div>
                      </div>
                    }
                    control={<Radio className='ml-auto' />}
                  />
                </MenuItem>
              ))}
            </RadioGroup>
          </>
        )}
        {header.filter && (
          <div
            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
              handleFilterOpen(event, header)
            }}
          >
            <MenuItem
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'row',
                paddingLeft: 0,
                paddingRight: 0,
              }}
              className='d-flex justify-content-between align-items-center'
            >
              <MUIGrid style={{ display: 'flex', alignItems: 'center' }}>
                <FilterListOutlinedIcon />
                <span
                  className='font-12p interFont'
                  style={{ fontWeight: '600', paddingLeft: '5px' }}
                >
                  Filter
                </span>
              </MUIGrid>
              <LastPageIcon />
            </MenuItem>
          </div>
        )}
      </Menu>
    ) : (
      <></>
    )
  }

  const setPageCount = () => {
    return Math.ceil((pagination?.totalCount ?? 1) / Number(pagination.pageLimit))
  }

  const getChildCellClass = (sticky: any, headerIndx: number, tableHeaders: any[]) => {
    if (sticky && headerIndx === getStickyIndex(sticky, tableHeaders)) {
      return 'custom-td-child list-sticky-header ' + css[`list_sticky_header_${sticky}`]
    }
    return 'custom-td-child'
  }
  const getClassName = (baseClass: any, additionalClass: any) => {
    return additionalClass ? `${baseClass} ${additionalClass}` : baseClass
  }
  const getTableCellContent = (row: any, header: any, rowURL: string) => {
    const content = (
      <div className={getClassName('paragraph-regular-6 interFont', header?.class)}>
        {renderTableValue(row, header)}
      </div>
    )

    if (row.rowURL) {
      if (header?.blitzId) {
        return content
      }
      return (
        <Link to={rowURL} style={{ textDecoration: 'none' }} className='table-link'>
          {content}
        </Link>
      )
    }

    return content
  }

  const getTableRows = (row: any, rowInd: number, isChild = false) => {
    const rowURL: string = row?.rowURL ?? ''
    const isRowClickable =
      rowsClickable || row.isRowClickable ? ` table-row-clickable ${css.tableRowClickable}` : ''
    return (
      <Fragment key={`main_${rowInd}`}>
        <TableRow
          data-testid='row-test'
          key={`${isChild ? 'child_' : ''}${rowInd}`}
          className={`customRowClickable${isRowClickable}`}
        >
          {tableHeaders?.map((header: any, headerIndx) => {
            if (header?.id === 'checkbox') {
              return (
                <TableCell
                  data-testid={'row-test-checkbox'}
                  className={getChildCellClass(sticky, 0, tableHeaders)}
                  style={{ width: '40px', minWidth: '40px' }}
                  key={`${isChild ? 'child_' : ''}${String(rowInd)}` + '_checkbox'}
                >
                  <MUITooltip title={checkboxTooltip?.row}>
                    <div>
                      <MUICheckbox
                        disabled={row.isCheckBoxDisabled}
                        checked={
                          row.isCheckBoxDisabled
                            ? false
                            : selectedRows?.includes(row?.[checkBoxKey])
                        }
                        handleChange={() => {
                          handleSelect(row)
                        }}
                      />
                    </div>
                  </MUITooltip>
                </TableCell>
              )
            }
            return (
              <TableCell
                data-testid={`row-test-${headerIndx}`}
                className={getChildCellClass(sticky, headerIndx, tableHeaders)}
                style={sticky ? { minWidth: stickyWidth } : {}}
                key={`${isChild ? 'child_' : ''}${String(rowInd)}` + '_' + String(headerIndx)}
                onClick={(e: any) => {
                  e.preventDefault()
                  if (archived) {
                    rowsClickable &&
                      onClickRow &&
                      rowInd === 0 &&
                      onClickRow({ row, rowInd, header, headerIndx, isChild })
                  } else {
                    rowsClickable &&
                      onClickRow &&
                      onClickRow({ row, rowInd, header, headerIndx, isChild })
                  }
                }}
              >
                {getTableCellContent(row, header, rowURL)}
              </TableCell>
            )
          })}
        </TableRow>
        {!isChild &&
          childRows?.index === rowInd &&
          Array.isArray(childRows?.records) &&
          childRows?.records?.length &&
          getChildRows(childRows.records, rowInd)}
      </Fragment>
    )
  }

  const getChildRows = (childRows: any, rowInd: number) => {
    return (
      <>
        <TableRow
          className='innerOpenedRow'
          key={`child_c_${rowInd}`}
          sx={{ maxWidth: '100% !important' }}
          data-testid='child-row-test'
        >
          <TableCell
            className='p-0 w-100'
            colSpan={tableHeaders.length}
            sx={{ maxWidth: '100% !important' }}
          >
            <Box
              sx={{ padding: '20px 0', maxWidth: '100% !important', backgroundColor: '#f5f4f4' }}
            >
              <Table size='small' aria-label='child-tabel' sx={{ backgroundColor: '#FFF' }}>
                <TableBody>
                  {childRows?.map((row: any, rowInd: number) => getTableRows(row, rowInd, true))}
                </TableBody>
              </Table>
            </Box>
          </TableCell>
        </TableRow>
      </>
    )
  }

  const getHeaderCellClass = (sticky: any, headerIndx: number, headers: any) => {
    if (sticky && headerIndx === getStickyIndex(sticky, headers)) {
      return 'list-sticky-header ' + css[`list_sticky_header_${sticky}`]
    }
    return ''
  }

  const getHeaderCellDivClass = (header: CommonTableHeaderModel) => {
    return header?.class ? `${css.tableHeader} ${header.class}` : css.tableHeader
  }

  const isSelectAllChecked =
    selectableRows?.length > 0 && selectableRows.length === selectedRows?.length

  return (
    <Grid
      sx={{
        width: '100%',
        backgroundColor: '#f5f4f4',
      }}
    >
      <TableContainer
        className={`custom-commonTable shadow-table ${css.tableContainer} ${className} ${
          transparent ? css.transparent : ''
        }`}
        style={{
          boxShadow: noBoxShadow ? 'none' : '0px 20px 24px -4px rgb(56 63 67 / 8%)',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders?.map((header: CommonTableHeaderModel, headerIndx) =>
                header?.id === 'checkbox' ? (
                  <TableCell
                    key='header_checkbox_select_all'
                    style={{ width: '40px', minWidth: '40px' }}
                  >
                    <div className={`${css.tableHeader} ${css.checkboxHeader}`}>
                      <MUITooltip title={checkboxTooltip?.header}>
                        <div>
                          <MUICheckbox
                            id='header_checkbox'
                            disabled={!selectableRows?.length}
                            checked={isSelectAllChecked}
                            handleChange={handleSelectAll}
                          />
                        </div>
                      </MUITooltip>
                    </div>
                  </TableCell>
                ) : (
                  <TableCell
                    key={`header_${String(headerIndx)}`}
                    className={getHeaderCellClass(sticky, headerIndx, tableHeaders)}
                    style={sticky ? { minWidth: stickyWidth } : {}}
                  >
                    <div className={getHeaderCellDivClass(header)}>
                      <TooltipText text={`${header.label || ''}`.toString()} length={28} />
                      {header?.icon ? header.icon : ''}
                      {(header.sort || header.filter) && (
                        <>
                          <MUITooltip title='Sort By' disableInteractive={true}>
                            <IconButton
                              data-testid='sort-filter'
                              className='m-4 d-flex align-items-center cursor-pointer'
                              size='small'
                              onClick={(ev) => handleSortFilterOpen(ev, header)}
                            >
                              <FilterSortIcon
                                header={header}
                                sortFilterKey={sortFilterKey}
                                selectedFilters={selectedFilters}
                              />
                            </IconButton>
                          </MUITooltip>
                          {sortFilterUi(header)}
                          {
                            <FilterUi
                              {...{
                                header,
                                filterOpen,
                                anchorEl2,
                                filterValue,
                                setFilterValue,
                                filterConOptions,
                                handleFilterClose,
                              }}
                            />
                          }
                        </>
                      )}
                    </div>
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row: any, rowInd: number) => getTableRows(row, rowInd))}
          </TableBody>
        </Table>
        {!rows.length && !loader && (
          <Typography className='d-flex justify-content-center' style={{ padding: '50px' }}>
            {noRecordsMessage}
          </Typography>
        )}
      </TableContainer>
      {pagination.show && (
        <MUIGrid
          container
          className='d-flex align-items-center justify-content-between mt-2 c-pagination'
        >
          {((!selectedFilters?.addArchivedData &&
            showArchivalHistory &&
            setPageCount() === pagination?.pageNumber) ||
            (!rows.length && showArchivalHistory)) && (
            <MUIGrid item xs={12} className='mtn-14'>
              <button
                className='fs12 btn-fetching-data col1 ml-auto mb-1'
                onClick={() => onChangeArchivalState(true)}
              >
                <span className='fs12 fetch-refresh-icon'>↻</span>
                {'   '}
                <span className='text-underline'>Fetch older data</span>
              </button>
            </MUIGrid>
          )}

          <MUIGrid item direction='row' className='d-flex align-items-center col1 c-dropdown'>
            <SimpleDropdown
              selectId='pageId'
              options={pagination.limitOptions}
              handleChange={(ev: Record<string, any>) =>
                pagination.onPageCountChange(ev.target.value)
              }
              value={pagination.pageLimit}
            />
            <Typography paddingX={3}>
              Showing{' '}
              {pagination.pageLimit > pagination.totalCount ? pagination.totalCount : rows.length}{' '}
              of {pagination.totalCount} records
            </Typography>
          </MUIGrid>
          <MUIGrid item>
            {pagination.totalCount > 0 && (
              <MUIPagination
                data-testid='common-pagination'
                onChange={(_ev, pageNumber) => pagination.onPageChange(pageNumber)}
                count={setPageCount()}
                page={pagination.pageNumber}
              />
            )}
          </MUIGrid>
        </MUIGrid>
      )}
    </Grid>
  )
}

export default MUICommonTable
